import { CurrencyCode, CurrencySymbol } from 'Utils/types/currencies'

export const numToCurrency = (
  amount: number,
  currencyCode: CurrencyCode,
  locale = 'en-US',
  showMinorUnit = true
) => {
  const hasMinorUnit = amount - Math.floor(amount) !== 0

  return (
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: showMinorUnit || hasMinorUnit ? 2 : 0,
      maximumFractionDigits: showMinorUnit || hasMinorUnit ? 2 : 0
    })
      .format(amount)
      // If the locale doesn't use the currency, convert the code to the symbol
      .replace(currencyCode, CurrencySymbol[currencyCode])
  )
}

export const roundAmount = (amount: number) => {
  // Note the plus sign that drops any "extra" zeroes at the end.
  // https://stackoverflow.com/a/12830454
  return +amount.toFixed(2)
}

const nonNumberRegex = /[^\d.-]/g
const nonNumberRegexNoNegative = /[^\d.]/g
const intercedingNegativeSign = /(.)-/g
const singleDecimalRegex = /^(-?\d*\.\d*)\./g
const doubleDecimalRegex = /^(-?\d*\.\d{2})\d+$/g
const groupedNumberRegex = /[.,]?\d(?:[\d., \u00a0\u2009\u202f'\u2019]*\d)?/g
const abbreviationDotRegex = /\p{L}+\./gu
const minusSignBeforeNumberRegex = /[-\u2212][^\dA-Za-z]*[.,]?\d/

const decimalSeparatorOf = (numberText: string) => {
  const separators = numberText.replace(/[^.,]/g, '')
  const last = separators.slice(-1)
  if (!last) return undefined

  const usesBothSeparators = separators.includes('.') && separators.includes(',')
  if (usesBothSeparators) return last

  const [whole, ...afterSeparator] = numberText.replace(/[^\d.,]/g, '').split(last)
  const appearsMoreThanOnce = afterSeparator.length > 1
  const readsAsThousands =
    last === ',' && afterSeparator[0].length === 3 && /^[1-9]\d{0,2}$/.test(whole)
  return appearsMoreThanOnce || readsAsThousands ? undefined : last
}

const isValidGrouping = (groups: string[]) =>
  groups.length === 1 ||
  (/^\d{1,3}$/.test(groups[0]) &&
    groups[groups.length - 1].length === 3 &&
    groups.slice(1, -1).every((group) => /^\d{2,3}$/.test(group)))

const roundDecimalTextToCents = (decimalText: string) =>
  Math.round(Number(`${decimalText}e2`)) / 100

export const parseAmount = (value: string): number | undefined => {
  const text = value.replace(abbreviationDotRegex, ' ')
  const numbers = text.match(groupedNumberRegex)
  if (numbers?.length !== 1) return undefined

  const [numberText] = numbers
  const decimalSeparator = decimalSeparatorOf(numberText)
  const [whole, fraction = '', ...extraDecimalParts] = decimalSeparator
    ? numberText.split(decimalSeparator)
    : [numberText]
  const wholeGroups = whole.split(/\D/)
  if (extraDecimalParts.length || /\D/.test(fraction) || !isValidGrouping(wholeGroups)) {
    return undefined
  }

  const unsigned = roundDecimalTextToCents(`${wholeGroups.join('')}.${fraction}`)
  return minusSignBeforeNumberRegex.test(text) ? -unsigned : unsigned
}

export const isAmount = (value: unknown) => parseAmount(`${value ?? ''}`) !== undefined

export const isNonZeroAmount = (value: unknown) => !!parseAmount(`${value ?? ''}`)

export const normalizeTypedAmount = (value: string, allowNegative = false) =>
  value
    .replace(allowNegative ? nonNumberRegex : nonNumberRegexNoNegative, '')
    .replace(intercedingNegativeSign, '$1')
    .replace(singleDecimalRegex, '$1')
    .replace(doubleDecimalRegex, '$1')

export const normalizePastedAmount = (value: string, allowNegative = false) => {
  const amount = parseAmount(value)
  if (amount === undefined) return value.trim()
  return `${allowNegative ? amount : Math.abs(amount)}`
}
